/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import RouterService from '@ember/routing/router-service';
import ApiErrorsService from 'cio/services/api-errors';
import SqlImportModel, { ImportStatus } from 'cio/models/sql-import';
import SegmentModel from 'cio/models/segment';
import SqlImportDatabaseModel from 'cio/models/sql-import-database';
import { SqlImportDatabase, SqlImportsCreateStep } from 'cio/utils/sql-import';
import { isEmpty } from '@ember/utils';
import AnalyticsService from 'cio/services/analytics';
import MomentService from 'cio/services/moment';
import ObjectTypesService from 'cio/services/object-types';
import isDestroyed from 'cio/utils/is-destroyed';
import SqlImportsCreateDetails from 'cio/components/sql-imports/create/details';
import SqlImportsCreateSettings from 'cio/components/sql-imports/create/settings';
import SqlImportsCreateDatabase from 'cio/components/sql-imports/create/database';
import SqlImportsCreateQuery from 'cio/components/sql-imports/create/query';
import SqlImportsCreateRelationshipsQuery from 'cio/components/sql-imports/create/relationships-query';
import SqlImportsCreateReview from 'cio/components/sql-imports/create/review';

interface SqlImportsCreateArgs {
  type: SqlImportDatabase;
  model: SqlImportModel;
  databases: SqlImportDatabaseModel[];
  segments: SegmentModel[];
}

export default class SqlImportsCreate extends Component<SqlImportsCreateArgs> {
  @service router!: RouterService;
  @service apiErrors!: ApiErrorsService;
  @service analytics!: AnalyticsService;
  @service moment!: MomentService;
  @service objectTypes!: ObjectTypesService;

  @tracked activeStepId = SqlImportsCreateStep.Details;
  @tracked hasSuccessfulQuery = false;
  @tracked hasSuccessfulRelationshipsQuery = false;
  @tracked databases = this.args.databases.toArray();

  showRelationshipsTab = this.objectTypes.hasObjectTypes;

  get activeStep() {
    return this.steps.find((step) => step.id === this.activeStepId);
  }

  get shouldExpandContent() {
    return [SqlImportsCreateStep.Query, SqlImportsCreateStep.RelationshipsQuery].includes(this.activeStepId);
  }

  @computed('args.model.name')
  get hasBasicDetails() {
    return !isEmpty(this.args.model.name);
  }

  @computed('args.model.{interval,idFieldName,startAt}', 'moment.currentTimestamp')
  get hasRequiredSettings() {
    return (
      !isEmpty(this.args.model.interval) &&
      !isEmpty(this.args.model.idFieldName) &&
      (this.args.model.startAt === 0 || this.args.model.startAt > this.moment.currentTimestamp)
    );
  }

  @computed('args.model.database.{name,host,port,username,type,projectId}')
  get hasDatabaseDetails() {
    if (isDestroyed(this.args.model)) {
      return false;
    }

    let database = this.args.model.belongsTo('database').value() as SqlImportDatabaseModel | null;

    if (database == null) {
      return false;
    }

    let requiredKeys = ['name', 'type', 'username', 'host', 'port'];

    if (database?.type === SqlImportDatabase.bigQuery) {
      requiredKeys = ['projectId'];
    }

    return !requiredKeys.some((attr: keyof SqlImportDatabaseModel) => isEmpty(database?.get(attr)));
  }

  @computed('args.model.query')
  get hasQuery() {
    return !isEmpty(this.args.model.query);
  }

  get steps() {
    let steps = [
      {
        id: SqlImportsCreateStep.Details,
        label: 'Sync',
        complete: this.hasBasicDetails,
        active: this.activeStepId === SqlImportsCreateStep.Details,
        component: SqlImportsCreateDetails,
      },
      {
        id: SqlImportsCreateStep.Settings,
        label: 'Settings',
        complete: this.hasRequiredSettings,
        active: this.activeStepId === SqlImportsCreateStep.Settings,
        component: SqlImportsCreateSettings,
      },
      {
        id: SqlImportsCreateStep.Database,
        label: 'Database',
        complete: this.hasDatabaseDetails,
        active: this.activeStepId === SqlImportsCreateStep.Database,
        component: SqlImportsCreateDatabase,
      },
      {
        id: SqlImportsCreateStep.Query,
        label: 'Query',
        complete: this.hasSuccessfulQuery,
        active: this.activeStepId === SqlImportsCreateStep.Query,
        component: SqlImportsCreateQuery,
      },
      {
        id: SqlImportsCreateStep.RelationshipsQuery,
        label: 'Relationships (optional)',
        complete: this.hasSuccessfulRelationshipsQuery,
        active: this.activeStepId === SqlImportsCreateStep.RelationshipsQuery,
        component: SqlImportsCreateRelationshipsQuery,
      },
      {
        id: SqlImportsCreateStep.Review,
        label: 'Review',
        complete: false,
        active: this.activeStepId === SqlImportsCreateStep.Review,
        component: SqlImportsCreateReview,
      },
    ];

    return steps
      .filter((step) => {
        return !(step.id === SqlImportsCreateStep.RelationshipsQuery && !this.showRelationshipsTab);
      })
      .map((step, index) => {
        return {
          ...step,

          // disabled if not complete and any of the previous steps isn't complete:
          disabled: !step.complete && steps.slice(0, index).some((step) => !step.complete),
        };
      });
  }

  @action
  changeStep(stepId: SqlImportsCreateStep) {
    this.activeStepId = stepId;
  }

  get nextStepLabel() {
    return {
      [SqlImportsCreateStep.Details]: 'Next',
      [SqlImportsCreateStep.Settings]: 'Next',
      [SqlImportsCreateStep.Database]: 'Next',
      [SqlImportsCreateStep.Query]: 'Next',
      [SqlImportsCreateStep.RelationshipsQuery]: 'Next',
      [SqlImportsCreateStep.Review]: 'Set up sync',
    }[this.activeStepId];
  }

  get cancelButtonLabel() {
    return {
      [SqlImportsCreateStep.Details]: 'Cancel',
      [SqlImportsCreateStep.Settings]: 'Back',
      [SqlImportsCreateStep.Database]: 'Back',
      [SqlImportsCreateStep.Query]: 'Back',
      [SqlImportsCreateStep.RelationshipsQuery]: 'Back',
      [SqlImportsCreateStep.Review]: 'Back',
    }[this.activeStepId];
  }

  @action
  nextStep() {
    switch (this.activeStepId) {
      case SqlImportsCreateStep.Details:
        this.activeStepId = SqlImportsCreateStep.Settings;
        break;

      case SqlImportsCreateStep.Settings:
        this.activeStepId = SqlImportsCreateStep.Database;
        break;

      case SqlImportsCreateStep.Database:
        this.activeStepId = SqlImportsCreateStep.Query;
        break;

      case SqlImportsCreateStep.Query:
        this.activeStepId = this.showRelationshipsTab
          ? SqlImportsCreateStep.RelationshipsQuery
          : SqlImportsCreateStep.Review;
        break;

      case SqlImportsCreateStep.RelationshipsQuery:
        this.activeStepId = SqlImportsCreateStep.Review;
        break;

      case SqlImportsCreateStep.Review:
        this.saveTask.perform();
        break;
    }
  }

  @action
  previousStep() {
    switch (this.activeStepId) {
      case SqlImportsCreateStep.Details:
        this.router.transitionTo('sql-imports');
        break;

      case SqlImportsCreateStep.Settings:
        this.activeStepId = SqlImportsCreateStep.Details;
        break;

      case SqlImportsCreateStep.Database:
        this.activeStepId = SqlImportsCreateStep.Settings;
        break;

      case SqlImportsCreateStep.Query:
        this.activeStepId = SqlImportsCreateStep.Database;
        break;

      case SqlImportsCreateStep.RelationshipsQuery:
        this.activeStepId = SqlImportsCreateStep.Query;
        break;

      case SqlImportsCreateStep.Review:
        this.activeStepId = this.showRelationshipsTab
          ? SqlImportsCreateStep.RelationshipsQuery
          : SqlImportsCreateStep.Query;
        break;
    }
  }

  @action
  onCreateDatabase(database: SqlImportDatabaseModel) {
    this.databases = [...this.databases, database];
  }

  @action
  onDeleteDatabase(database: SqlImportDatabaseModel) {
    this.databases = this.databases.filter((d) => d.id !== database.id);
  }

  @action
  onQuery({
    hasSuccessfulQuery,
    hasSuccessfulRelationshipsQuery,
  }: {
    hasSuccessfulQuery: boolean;
    hasSuccessfulRelationshipsQuery?: boolean;
  }) {
    this.hasSuccessfulQuery = hasSuccessfulQuery;
    this.hasSuccessfulRelationshipsQuery = !!hasSuccessfulRelationshipsQuery;
  }

  saveTask = dropTask(async () => {
    try {
      this.args.model.set('ImportStatus', ImportStatus.Published);
      await this.args.model.save();
      this.analytics.track('sql_imports', 'create_sync_started', { model: this.args.model });
      this.router.transitionTo('sql-imports.import', this.args.model.id);
    } catch (err) {
      this.apiErrors.report(err);
    }
  });
}
